import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, Send, X, Loader2, Sparkles, FileText, Calendar, MessageCircleQuestion, ClipboardCheck, Upload } from "lucide-react";
import { TID } from "@/constants/testIds";
import { API_BASE } from "@/lib/api";

const SUGGESTIONS = [
  { icon: ClipboardCheck, t: "Request Consultation", q: "I'd like to request a consultation with a Clinivon consultant." },
  { icon: MessageCircleQuestion, t: "Ask a Question", q: "I have a question about your services." },
  { icon: Upload, t: "Upload a Contract", q: "I'd like to have a contract reviewed by Clinivon." },
  { icon: Calendar, t: "Schedule a Meeting", q: "Help me schedule a meeting with a consultant." },
  { icon: FileText, t: "Request a Process Assessment", q: "I'd like a process assessment for my business." },
];

export default function ChatWidget({ onOpenForm }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Clinivon's AI assistant. How can we help you today?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(`web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      const node = scrollRef.current;
      if (node) node.scrollTop = node.scrollHeight;
    });
  };

  const send = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: "user", content: text };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "" }]);
    setInput("");
    setLoading(true);
    scrollToBottom();

    try {
      const res = await fetch(`${API_BASE}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("Chat unavailable");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
              scrollToBottom();
            }
            if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "Sorry — I'm temporarily unavailable. Please use the form below or call 942-995-1975." };
                return copy;
              });
            }
          } catch (_) { /* ignore malformed chunk */ }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — I'm temporarily unavailable. Please use the form below or call 942-995-1975." };
        return copy;
      });
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  };

  return (
    <>
      <motion.button
        data-testid={TID.chat.fab}
        onClick={() => setOpen(!open)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.25 }}
        className="fixed bottom-6 right-6 z-50 group"
        aria-label="Open chat"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-[#10B981] rounded-full blur-xl opacity-50 group-hover:opacity-70 transition-opacity" />
          <div className="relative h-14 w-14 rounded-full bg-[#0A2540] text-white grid place-items-center shadow-2xl hover:bg-[#061A30] transition-colors">
            {open ? <X size={22} /> : <MessageSquare size={22} />}
          </div>
          {!open && (
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-[#10B981] rounded-full ring-2 ring-white animate-pulse" />
          )}
        </div>
        {!open && (
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-[#0A2540] text-white text-xs font-semibold px-3 py-1.5 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
            Need Help?
          </span>
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={TID.chat.panel}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] glass rounded-sm shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="bg-[#0A2540] text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 grid place-items-center bg-[#10B981] rounded-full"><Sparkles size={16} /></div>
                <div>
                  <div className="font-display font-semibold text-sm">Clinivon AI Assistant</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/60 flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 bg-[#10B981] rounded-full" /> Online
                  </div>
                </div>
              </div>
              <button data-testid={TID.chat.close} onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded"><X size={16} /></button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-white">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#0A2540] text-white" : "bg-slate-100 text-slate-800"}`}>
                    {m.content || (loading && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin" /> : "")}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="pt-2 space-y-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s.t}
                      onClick={() => {
                        if (s.t === "Request Consultation" || s.t === "Upload a Contract" || s.t === "Request a Process Assessment") {
                          setOpen(false);
                          onOpenForm?.();
                          return;
                        }
                        send(s.q);
                      }}
                      data-testid={s.t === "Request Consultation" ? TID.chat.openForm : undefined}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs border border-slate-200 hover:border-[#0A2540] hover:bg-slate-50 transition-colors text-left text-slate-700"
                    >
                      <s.icon size={14} className="text-[#10B981]" />
                      {s.t}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(input); }}
              className="border-t border-slate-200 p-3 flex items-center gap-2 bg-white"
            >
              <input
                data-testid={TID.chat.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message…"
                disabled={loading}
                className="flex-1 px-3 py-2 text-sm bg-slate-50 border border-slate-200 focus:border-[#0A2540] focus:outline-none rounded-sm"
              />
              <button
                data-testid={TID.chat.send}
                disabled={loading || !input.trim()}
                className="h-9 w-9 grid place-items-center bg-[#0A2540] text-white disabled:opacity-50 hover:bg-[#061A30] transition-colors rounded-sm"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
